import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  CheckCircle2, 
  Clock, 
  ChevronRight,
  User,
  FileSignature,
  Upload,
  AlertCircle,
  Wallet,
  FileText,
  Shield
} from 'lucide-react';

const WhatsNextCard = ({ user, commitments = [], signatureRequests = [] }) => {
  // Don't show for admins
  const isAdmin = ['super_admin', 'fund_director', 'analyst', 'compliance'].includes(user?.role);
  if (isAdmin) return null;

  // Determine what the user needs to do next
  const kycStatus = user?.verification_status;
  const profileCompletion = user?.profile_completion || 0;
  
  const pendingSignatures = signatureRequests.filter(s => ['pending', 'viewed'].includes(s.status));
  const awaitingFundingCommitments = commitments.filter(c => c.status === 'approved' && !c.proof_of_transfer_doc_id);
  const pendingSignatureCommitments = commitments.filter(c => c.status === 'awaiting_signature');

  // Priority order of next actions
  const getNextAction = () => {
    // 1. Complete Profile / KYC
    if (kycStatus !== 'approved' && profileCompletion < 100) {
      return {
        priority: 'high',
        icon: User,
        title: 'Complete su Perfil',
        description: `Su perfil está al ${profileCompletion}%. Complete todos los campos para poder invertir.`,
        action: '/app/profile',
        actionLabel: 'Completar Perfil',
        status: 'required'
      };
    }

    // 2. KYC pending approval
    if (kycStatus === 'pending') {
      return {
        priority: 'medium',
        icon: Shield,
        title: 'Verificación en Proceso',
        description: 'Su perfil está siendo revisado por nuestro equipo. Le notificaremos cuando sea aprobado.',
        action: '/app/profile',
        actionLabel: 'Ver Estado',
        status: 'waiting'
      };
    }

    // 3. Sign pending documents
    if (pendingSignatures.length > 0 || pendingSignatureCommitments.length > 0) {
      const count = pendingSignatures.length || pendingSignatureCommitments.length;
      const sigId = pendingSignatures[0]?.request_id || pendingSignatureCommitments[0]?.signature_request_id;
      return {
        priority: 'high',
        icon: FileSignature,
        title: 'Firmar Acuerdo de Inversión',
        description: `Tiene ${count} documento(s) pendiente(s) de firma. Complete la firma para continuar.`,
        action: sigId ? `/app/signatures/${sigId}` : '/app/signatures',
        actionLabel: 'Firmar Ahora',
        status: 'action_required'
      };
    }

    // 4. Upload proof of funds
    if (awaitingFundingCommitments.length > 0) {
      return {
        priority: 'high',
        icon: Upload,
        title: 'Subir Comprobante de Fondeo',
        description: `Tiene ${awaitingFundingCommitments.length} inversión(es) aprobada(s) esperando fondos. Realice la transferencia y suba su comprobante.`,
        action: '/app/investments',
        actionLabel: 'Ver Inversiones',
        status: 'action_required'
      };
    }

    // 5. Explore opportunities (default for approved users)
    if (kycStatus === 'approved') {
      return {
        priority: 'low',
        icon: Wallet,
        title: 'Explorar Oportunidades',
        description: 'Su perfil está aprobado. Explore nuestras oportunidades de inversión disponibles.',
        action: '/app/opportunities',
        actionLabel: 'Ver Oportunidades',
        status: 'ready'
      };
    }

    return null;
  };

  const nextAction = getNextAction();

  if (!nextAction) return null;

  const priorityColors = {
    high: 'border-amber-500/30 bg-amber-500/5',
    medium: 'border-blue-500/30 bg-blue-500/5',
    low: 'border-primary/30 bg-primary/5'
  };

  const statusBadges = {
    required: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Requerido' },
    waiting: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'En Proceso' },
    action_required: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Acción Requerida' },
    ready: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Listo' }
  };

  const Icon = nextAction.icon;
  const badgeStyle = statusBadges[nextAction.status];

  return (
    <Card className={`card-dashboard ${priorityColors[nextAction.priority]}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            Siguiente Paso
          </CardTitle>
          <Badge variant="outline" className={badgeStyle.className}>
            {badgeStyle.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-background border border-border">
            <Icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-lg mb-1">{nextAction.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {nextAction.description}
            </p>
            <Link to={nextAction.action}>
              <Button className="btn-primary gap-2">
                {nextAction.actionLabel}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatsNextCard;
